import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from "react-router-dom";

const Header = () => {
    const [open, setOpen] = useState(false);
    const [widthPct, setWidthPct] = useState(80);
    const ticking = useRef(false);

    const location = useLocation();

    const toggleMenu = () => setOpen(prev => !prev);
    const closeMenu = () => setOpen(false);

    const links = [
        {to: "/systems", label: "Systems"},
        {to: "/dev", label: "Development"},
        {to: "/studios", label: "Studios"},
    ];

    useEffect(() => {
      const header = document.querySelector('header.header') as HTMLElement | null;
      let rafId: number | null = null;

      const computeAndSet = () => {
        const doc = document.documentElement;
        const st = doc.scrollTop || document.body.scrollTop || 0;
        const maxScroll = (doc.scrollHeight - doc.clientHeight) || 1;
        const progress = Math.min(1, Math.max(0, st / maxScroll));

        if (progress <= 0.10) {
          setWidthPct(80);
        } else {
          const start = 0.15;
          const end = 0.25;
          const t = Math.min(1, Math.max(0, (progress - start) / (end - start)));
          const w = 80 - (25 * t);
          setWidthPct(w);
        }

        if (header) {
          header.style.left = '50%';
          header.style.transform = 'translateX(-50%)';
          header.style.top = '0';
        }
      };

      const loop = () => {
        computeAndSet();
        rafId = requestAnimationFrame(loop);
      };

      const stopLoop = () => {
        if (rafId) {
          cancelAnimationFrame(rafId);
          rafId = null;
        }
      };

      const applyStaticWidth = (pct: number) => {
        setWidthPct(pct);
        if (header) {
          header.style.left = '50%';
          header.style.transform = 'translateX(-50%)';
          header.style.top = '0';
        }
      };

      const init = () => {
        stopLoop();
        const isMobile = window.innerWidth < 700; // disable shrinking on small screens

        if (isMobile) {
          applyStaticWidth(74); // keep a pleasant margin on small screens
          return;
        }

        // For all other routes, run the loop as before
        loop();
      };

      init();

      const onResize = () => {
        init();
      };

      window.addEventListener('resize', onResize);
      return () => {
        stopLoop();
        window.removeEventListener('resize', onResize);
      };
    }, [location.pathname]);

    return (
        <header className="header" role="banner" style={{ width: `${widthPct}%` }}>
            <Link to={"/"} className="logo" onClick={closeMenu} style={{ display: "flex" }}>
                <img src="/pics/logo/sec/dd_logo_colors_sec.svg" alt=""/>
            </Link>

            {/* Desktop nav */}
            <nav className="nav-desktop" aria-label="Hauptnavigation">
                <ul>
                    {links.map(l => (
                        <li key={l.label}><Link to={l.to}>{l.label}</Link></li>
                    ))}
                </ul>
            </nav>

            {/* Mobile menu toggle */}
            <button
                className="menu-toggle"
                aria-label={open ? "Menü schließen" : "Menü öffnen"}
                aria-expanded={open}
                aria-controls="mobile-menu-panel"
                onClick={toggleMenu}
            >
                <i className={open ? "bi bi-x-lg" : "bi bi-list"}/>
            </button>

            {/* Backdrop */}
            {open && <div className="backdrop" onClick={closeMenu} aria-hidden="true"></div>}

            {/* Sliding mobile panel */}
            <aside
                id="mobile-menu-panel"
                className={`mobile-menu ${open ? "open" : ""}`}
                role="dialog"
                aria-modal="true"
            >
                <div className="mobile-header">
                    <Link to={"/"} className="logo" onClick={closeMenu}>
                        <img src="/pics/logo/sec/dd_logo_mono_sec.svg" alt="das-TragWerk"/>
                    </Link>
                    <button className="menu-close" aria-label="Menü schließen" onClick={closeMenu}>
                        <i className="bi bi-x-lg"/>
                    </button>
                </div>
                <nav className="nav-mobile" aria-label="Mobiles Menü">
                    <ul>
                        {links.map(l => (
                            <li key={`m-${l.label}`}>
                                <Link to={l.to} onClick={closeMenu}>{l.label}</Link>
                            </li>
                        ))}
                    </ul>
                </nav>
            </aside>

            <button id="contact-button">
                <Link to="/" state={{scrollTo: 'contact-section'}}>Get in Touch</Link>
            </button>
        </header>
    );
};

export default Header;